import React, { useRef, useState, useMemo, useEffect } from 'react';
import Typography from '@material-ui/core/Typography';
import { useQuery } from '@tanstack/react-query';
import { useVirtualizer } from '@tanstack/react-virtual';
import clsx from 'clsx';
import compact from 'lodash/compact';
import intersection from 'lodash/intersection';
import map from 'lodash/map';
import get from 'lodash/get';
import { getSRLSurveyTableData } from '../../../../../core/api';
import {
  ASSIGNMENT_DETAILS_TABLE,
  isAssignmentPerfWidget,
  SRL_DETAILS_TABLE,
  WIDGET_TYPE_SRL_CHALLENGES_CHART,
} from '../../../../constants';
import { sortStudentsByName } from '../../../../utils';
import MLLoading from '../../../../../../components/MLLoading';
import MLButton from '../../../../../../components/MLButton';
import MLToggle from '../../../../../../components/MLToggle';
import { getBrand } from '../../../../utils';
import { BRANDS } from '../../../../constants';
import ColumnHeader from './components/ColumnHeader/ColumnHeader';
import { useIsOverflow } from './hooks/useIsOverflow';
import { useTableData } from './hooks/useTableData';
import SearchDropdown from './components/SearchDropdown/SearchDropdown';
import SearchStudent from './components/SearchStudent/SearchStudent';

import s from './SRLVirtualTable.module.css';
import TagsFilter from './components/TagsFilter/TagsFilter';

function VirtualRow({
  row,
  virtualRow,
  allCollapsed,
  isExpanded,
  toggleRow,
  columns,
  virtualizer,
  hasResponsesColumn = false,
}) {
  const expanded = !allCollapsed || isExpanded;
  const [showViewMore, setShowViewMore] = useState(false);
  const [showViewLess, setShowViewLess] = useState(false);
  const [rowOverflow, setRowOverflow] = useState(false);
  const hasOverflowCallback = (hasOverflow) => {
    setRowOverflow(hasOverflow);
  };
  const { ref: rowRef } = useIsOverflow(hasOverflowCallback, 22);

  // Keeps track of the overflow element when is not expanded
  useEffect(() => {
    if (rowOverflow && !isExpanded) {
      setShowViewMore(true);
    } else {
      setShowViewMore(false);
    }
  }, [rowOverflow, isExpanded]);

  // Keeps track of the 'View Less' button once the element is expanded and loses overflow
  useEffect(() => {
    if (isExpanded && allCollapsed) {
      setShowViewLess(true);
    } else {
      setShowViewLess(false);
    }
  }, [isExpanded, allCollapsed]);

  return (
    <div
      role="row"
      className={s.virtualRowContainer}
      key={virtualRow.key}
      data-index={virtualRow.index}
      ref={virtualizer.measureElement}
    >
      <div
        ref={rowRef}
        className={s.overflowRowContainer}
        style={{ height: expanded ? 'auto' : 96 }}
      >
        {columns.map((column) => (
          <div
            role="gridcell"
            className={s.cellRow}
            key={column.name}
            style={{
              width: column.width,
              flexBasis: column.flexBasis,
              ...(column.width && { flexShrink: 0 }),
              ...(!column.width && { minWidth: 0 }),
              ...(column.centerText && { textAlign: 'center' }),
            }}
          >
            {column.render(row)}
          </div>
        ))}
      </div>
      {hasResponsesColumn && (
        <div className={s.viewMoreContainer}>
          {(showViewMore || showViewLess) && (
            <MLButton
              className={s.viewMoreButton}
              onClick={() => toggleRow(virtualRow.index)}
            >
              {isExpanded ? 'View Less' : 'View More'}
            </MLButton>
          )}
        </div>
      )}
    </div>
  );
}

function VirtualTable({
  data,
  widgetType,
  isSRLIntroSurvey,
  isSRLEnglishVersion,
  TABLE_CONSTANTS,
  allCollapsed,
  hasResponsesColumn,
  searchResponseTerm,
  selectedTags,
  insightsData,
  courseId,
  courseShortId,
  productModelId,
  assignmentId,
  activityId,
  tool,
}) {
  const tableSortIndex = get(TABLE_CONSTANTS, "DEFAULT_SORT_INDEX", 0);
  const [selectedSortSettings, setSelectedSortSettings] = useState({
    sortColumn:
      tableSortIndex === 0 ? "student" : `answer_${tableSortIndex - 1}`,
    sortDirection:
      TABLE_CONSTANTS.DEFAULT_SORT_DIRECTION ||
      SRL_DETAILS_TABLE.SORT_ASCENDING,
  });

  const [expandedRows, setExpandedRows] = useState([]);

  useEffect(() => {
    if (allCollapsed) {
      setExpandedRows([]);
    }
  }, [allCollapsed]);

  const { columns, rows, count, positiveCounters, confidenceSubHeaders, hoursSubHeaders } = useTableData({
    TABLE_CONSTANTS,
    initialColumns: data.columns,
    initialRows: data.data,
    widgetType,
    isSRLIntroSurvey,
    isSRLEnglishVersion,
    selectedSortSettings,
    searchResponseTerm,
    selectedTags,
    insightsData,
    courseId,
    courseShortId,
    productModelId,
    assignmentId,
    activityId,
    tool,
  });

  const parentRef = useRef(null);
  const virtualizer = useVirtualizer({
    count,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 98,
    overscan: 10,
  });

  const items = virtualizer.getVirtualItems();

  const toggleRow = (index) => {
    setExpandedRows((prev) => {
      if (prev.includes(index)) {
        return prev.filter((i) => i !== index);
      } else {
        return [...prev, index];
      }
    });
  };

  const handleSortClick = (column) => {
    const { sortColumn, sortDirection } = selectedSortSettings;
    let newSortDirection = SRL_DETAILS_TABLE.SORT_ASCENDING;
    if (column === sortColumn) {
      newSortDirection =
        sortDirection === SRL_DETAILS_TABLE.SORT_ASCENDING
          ? SRL_DETAILS_TABLE.SORT_DESCENDING
          : SRL_DETAILS_TABLE.SORT_ASCENDING;
    }
    setSelectedSortSettings({
      sortColumn: column,
      sortDirection: newSortDirection,
    });
  };

  return (
    <div className={s.tableContainer} role="table">
      <div role="rowgroup" ref={parentRef} className={s.tableBody}>
        <div role="row" className={s.columns}>
          {columns.map((column) => (
            <ColumnHeader
              key={column.name}
              headerSubText={positiveCounters[column.accessor]}
              confidenceSubHeaders={confidenceSubHeaders?.[column?.accessor]}
              hoursSubHeaders={hoursSubHeaders?.[column?.accessor]}
              column={column}
              handleSortClick={handleSortClick}
              selectedSortSettings={selectedSortSettings}
            />
          ))}
        </div>
        <div
          className={s.tableVirtualizerSize}
          style={{ height: virtualizer.getTotalSize() }}
        >
          <div
            className={s.rowsContainer}
            style={{ transform: `translateY(${items[0]?.start ?? 0}px)` }}
          >
            <div>
              {items.map((virtualRow) => {
                const isExpanded = expandedRows.includes(virtualRow.index);
                return (
                  <VirtualRow
                    key={virtualRow.key}
                    virtualizer={virtualizer}
                    virtualRow={virtualRow}
                    row={rows[virtualRow.index]}
                    columns={columns}
                    allCollapsed={allCollapsed}
                    isExpanded={isExpanded}
                    toggleRow={toggleRow}
                    hasResponsesColumn={hasResponsesColumn}
                    selectedTags={selectedTags}
                  />
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Search({
  data,
  onStudentSelected,
  selectedStudentId,
  hasResponsesColumn,
  allCollapsed,
  toggleAllRows,
  searchType,
  handleDropdown,
  handleResponseSearch,
  searchResponseTerm,
  hasTagsColumn,
  selectedTags,
  setSelectedTags,
}) {
  const searchData = useMemo(() => {
    const students = data.unfilteredData.map(({ student }) => ({
      ...student,
      name: `${student.firstName} ${student.lastName}`,
    }));
    return sortStudentsByName(students);
  }, [data.unfilteredData]);
  return (
    <div className={s.searchContainer}>
      {hasResponsesColumn ? (
        <SearchDropdown
          searchType={searchType}
          handleDropdown={handleDropdown}
          searchData={searchData}
          selectedStudentId={selectedStudentId}
          onStudentSelected={onStudentSelected}
          handleResponseSearch={handleResponseSearch}
          searchResponseTerm={searchResponseTerm}
        />
      ) : (
        <SearchStudent
          students={searchData}
          onStudentSelected={onStudentSelected}
        />
      )}
      {
        hasTagsColumn && (
          <TagsFilter
            selectedTags={selectedTags}
            setSelectedTags={setSelectedTags}
            data={data}
          />
        )
      }
      {hasResponsesColumn && (
        <div className={s.toggleContainer}>
          <p
            className={clsx(s.toggleLabel, {
              [s.activeToggle]: allCollapsed,
            })}
          >
            {`${allCollapsed ? "Expand Responses" : "Collapse Responses"}`}
          </p>
          <MLToggle
            checked={allCollapsed}
            onChange={toggleAllRows}
            testId="collapse_responses_toggle"
          />
        </div>
      )}
    </div>
  );
}

const transformChallengesData = (resultData = {}, cardData) => {
  // Tag mappings from cardData
  // answer_0 → “Instructor/ Instructional”
  // answer_1 → “Environmental”
  // answer_2 → “Access/ Technical”
  // answer_3 → “Course/ Content”
  // answer_4 → “Life Stressors/ Conflicts”
  // answer_5 → “Health”
  // answer_6 → “Academic/ Learning”
  // answer_7 → “Other”

  // Other mappings
  // answer_8 → Explanation  (column 2)
  // student -> Student data (column 0)

  const options = resultData.columns?.slice(1, -1);
  return map(cardData, (response) => {
    const selectedOptions = compact(map(options, (option, index) => {
      const answerKey = `answer_${index}`;
      return response[answerKey] === 'Yes' ? option : null;
    }));

    return {
      student: response.student,
      answer_0: selectedOptions,
      answer_1: response.answer_8,
    };
  }).map(x => {
    if (!x.answer_0.length) {
      if (!x.answer_1) {
        return {
          ...x,
          answer_0: '--'
        };
      } else {
        return {
          ...x,
          answer_0: 'No challenges'
        };
      }
    }
    return x;
  });
};

function SRLVirtualTable({ widget, courseId, courseShortId, productModelId, assignmentId, activityId, tool, isSRLIntroSurvey, isSRLEnglishVersion, insightsData }) {
  const cardId = widget.card_id;
  const widgetType = widget.widgetType;

  let TABLE_CONSTANTS = ASSIGNMENT_DETAILS_TABLE || {};
  if (!isAssignmentPerfWidget(widgetType)) {
    TABLE_CONSTANTS = SRL_DETAILS_TABLE[widgetType][isSRLIntroSurvey || false] || {};
  }

  const [allCollapsed, setAllCollapsed] = useState(false);
  const [selectedStudentId, setSelectedStudentId] = useState(null);
  const [searchResponseTerm, setSearchResponseTerm] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [searchType, setSearchType] = useState({
    label: 'Response',
    value: 'response',
  });

  let hasResponsesColumn = TABLE_CONSTANTS.RESPONSES_COLUMN_IDX;
  const hasTagsColumn = TABLE_CONSTANTS.TAGS_COLUMN_IDX;
  const responsesAnswerIndex = TABLE_CONSTANTS.RESPONSES_ANSWER_POSITION || TABLE_CONSTANTS.RESPONSES_COLUMN_IDX;

  const { data, isLoading: isLoadingTableData } = useQuery({
    queryKey: ["srlTableData", courseId, assignmentId, cardId],
    queryFn: () => getSRLSurveyTableData(courseId, assignmentId, cardId),
    select: (results) => {
      let filteredData = results.data;
      if (searchType.value === 'response' && searchResponseTerm !== '') {
        filteredData = filteredData.filter((row) => {
          const response = row[`answer_${responsesAnswerIndex - 1}`];
          return response && response.toLowerCase().includes(searchResponseTerm.toLowerCase());
        });
      }

      if (searchType.value === 'student' && selectedStudentId) {
        filteredData = filteredData.filter(({ student }) => student.studentId === selectedStudentId);
      }

      if (widgetType === WIDGET_TYPE_SRL_CHALLENGES_CHART && !isSRLIntroSurvey) {
        filteredData = transformChallengesData(results, filteredData);
        const challengesColumns = ['Student', 'Challenges'];

        // Highschool does not get an 'Explanations' column
        const brand = getBrand();
        if (brand !== BRANDS.HIGH_SCHOOL) {
          challengesColumns.push('Explanations');
        } else {
          hasResponsesColumn = false;
        }

        if (selectedTags.length) {
          filteredData = filteredData.filter((row) => {
            if (intersection(row.answer_0, selectedTags).length > 0) {
              return true;
            }

            if (selectedTags.includes('No challenges') && row.answer_0 === 'No challenges') {
              return true;
            }

            return false;
          });
        }

        return {
          columns: challengesColumns,
          data: filteredData,
          unfilteredData: results.data,
          tagOptions: results.columns?.slice(1, -1),
        };
      }

      return {
        columns: results.columns,
        data: filteredData,
        unfilteredData: results.data,
      };
    },
  });

  if (isLoadingTableData) {
    return (
      <div className={s.loader}>
        <MLLoading />
      </div>
    );
  }

  const toggleAllRows = () => {
    setAllCollapsed((prev) => !prev);
  };

  const handleDropdown = (item) => {
    setSearchType(item);
    if (searchType.value !== 'student') {
      setSearchResponseTerm('');
    } else {
      setSelectedStudentId(null);
    }
  };

  const handleResponseSearch = (value) => {
    setSearchResponseTerm(value);
  };

  const onStudentSelected = (option) => {
    const studentId = option && option.studentId;
    if (!studentId) {
      setSelectedStudentId(null);
      return;
    }
    if (searchType.value !== 'student') {
      setSearchType({ value: 'student', label: 'Student' });
    }
    setSelectedStudentId(studentId);
  };

  return (
    <div>
      <Typography classes={{ root: s.title }} variant="h5" color="textPrimary">
        Student Responses
      </Typography>
      <Search
        data={data}
        onStudentSelected={onStudentSelected}
        selectedStudentId={selectedStudentId}
        hasResponsesColumn={hasResponsesColumn}
        allCollapsed={allCollapsed}
        toggleAllRows={toggleAllRows}
        searchType={searchType}
        handleDropdown={handleDropdown}
        handleResponseSearch={handleResponseSearch}
        searchResponseTerm={searchResponseTerm}
        selectedTags={selectedTags}
        setSelectedTags={setSelectedTags}
        hasTagsColumn={hasTagsColumn}
      />
      <VirtualTable
        data={data}
        widgetType={widgetType}
        isSRLIntroSurvey={isSRLIntroSurvey}
        isSRLEnglishVersion={isSRLEnglishVersion}
        allCollapsed={allCollapsed}
        TABLE_CONSTANTS={TABLE_CONSTANTS}
        hasResponsesColumn={hasResponsesColumn}
        searchResponseTerm={searchResponseTerm}
        selectedTags={selectedTags}
        insightsData={insightsData}
        courseId={courseId}
        courseShortId={courseShortId}
        productModelId={productModelId}
        assignmentId={assignmentId}
        activityId={activityId}
        tool={tool}
      />
    </div>
  );
}

export default SRLVirtualTable;
